import { FC, useEffect, useRef, useState } from 'react';

import { Global } from '@emotion/react';
import { Grid, useTheme, Box, Typography, SwipeableDrawer, CssBaseline, Drawer } from '@mui/material';

import {
	Maybe,
	Asset,
	BffFacility,
	Facility,
	GoogleMapTemplateNoResultsFoundMessage,
	GoogleMapTemplateSingleComingSoonMessage,
	ContentTypeRichTextContent,
	Button,
	MultiCardV2 as TMultiCardV2,
	ImageAsset as TImageAsset,
	Tabs,
} from '@/types/generated';
import MultiCardV2 from '@/components/MultiCard';
import { IMultiCard } from '@/components/MultiCard/MultiCard';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import { SchedulingSystemType } from '@/types';
import { windowScrollToElement } from '@/utils/windowScrollToElement/windowScrollToElement';

import { IFacilitiesData } from '../GoogleMap';
import { RequestStatus } from '../GoogleMap.helpers';
import { IGoogleMapsSection } from '../StandardGoogleMap/GoogleMapSection/GoogleMapSection';
import { getMapAndLocationsComponentStyles } from '../GoogleMap.styles';
import TAGButton from '../../TAGButton';

import LocationSearch from './LocationSearch/LocationSearch';
import LocationCard from './LocationCard';
import ModernGoogleMapSection from './ModernGoogleMapSection';
import LoadingSkeleton from './LoadingSkeleton/LoadingSkeleton';
import {
	getLocationsCardContainerStyles,
	locationsListStyles,
	getListTogglerStyles,
	getLocationsWrapStyles,
	pullerBoxStyles,
	drawerStyles,
} from './ModernGoogleMap.styles';
import NoResultsView from './NoResultsView/NoResultsView';
interface INearbyLocationsCards {
	nearByFacilities: BffFacility[];
	defaultImage?: Maybe<Asset>;
	selectedImage?: Maybe<Asset>;
	googleMapsBusinessQuery?: Maybe<string>;
	facilityBasePath: string;
	isSmallScreen: boolean;
	holidayHoursMessageText?: Maybe<string>;
	noResultsFoundMessage?: Maybe<GoogleMapTemplateNoResultsFoundMessage>;
	singleComingSoonMessage?: Maybe<GoogleMapTemplateSingleComingSoonMessage>;
	pilotLocations: Set<string>;
	facilityBadgeLabel?: Maybe<string>;
	facilityButtonLabel?: Maybe<string>;
	scheduleButton?: Maybe<Button>;
	officeDetailsButton?: Maybe<Button>;
	selectedFacilityId?: string | null;
	setSelectedFacilityId?: (facilityId: string | null) => void;
	noResultsFoundImage?: Maybe<TImageAsset>;
	availableServicesTabs?: Maybe<Tabs>;
	listContainerRef?: React.RefObject<HTMLDivElement>;
}

const NearbyLocationsCards: FC<INearbyLocationsCards> = ({
	nearByFacilities,
	defaultImage,
	selectedImage,
	googleMapsBusinessQuery,
	facilityBasePath,
	isSmallScreen,
	holidayHoursMessageText,
	noResultsFoundMessage,
	singleComingSoonMessage,
	pilotLocations,
	facilityBadgeLabel,
	facilityButtonLabel,
	scheduleButton,
	officeDetailsButton,
	selectedFacilityId,
	setSelectedFacilityId,
	noResultsFoundImage,
	availableServicesTabs,
	listContainerRef,
}) => {
	const theme = useTheme();
	const hasSelectedFacility = !!selectedFacilityId;
	const [showLocationListOnMobile, setShowLocationListOnMobile] = useState<boolean>(false);
	const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
	const [touchStart, setTouchStart] = useState<number | null>(null);

	useEffect(() => {
		if (hasSelectedFacility && !showLocationListOnMobile) {
			setShowLocationListOnMobile(true);
		}
		setIsFullScreen(hasSelectedFacility);
	}, [hasSelectedFacility, showLocationListOnMobile]);

	if (nearByFacilities?.length === 0) {
		return (
			<NoResultsView
				viewImage={noResultsFoundImage}
				viewMessage={noResultsFoundMessage as ContentTypeRichTextContent}
			/>
		);
	}

	const comingSoonLocation = nearByFacilities.find((nbF) => nbF.comingSoon === true);
	const hasSingleComingSoonFacility = !!comingSoonLocation && nearByFacilities.length === 1;
	const unselectFacility = () => setSelectedFacilityId && setSelectedFacilityId(null);

	if (hasSingleComingSoonFacility) {
		const locationId = comingSoonLocation.code.split('_')[1];

		return (
			<NoResultsView
				viewImage={noResultsFoundImage}
				viewMessage={singleComingSoonMessage as ContentTypeRichTextContent}
				locationId={locationId}
			/>
		);
	}
	const locationCounter = (
		<Typography variant="bodySmallBook">
			1-{nearByFacilities.length} of {nearByFacilities.length} near me
		</Typography>
	);

	const listToggler = (
		<Box sx={getListTogglerStyles({ theme })}>
			{showLocationListOnMobile && !hasSelectedFacility ? <Box sx={pullerBoxStyles} /> : null}

			{hasSelectedFacility ? (
				<TAGButton
					size="XS"
					variant="primaryHC"
					onClick={unselectFacility}
					startIcon={<TAGSvgIcon icon="West" size={16} />}
					dataTestId="modern_google_maps_location_list_back_button"
				>
					<Typography variant="captionMedium" color="text.interactive">
						Back to All Locations
					</Typography>
				</TAGButton>
			) : (
				<>
					{locationCounter}
					<TAGButton
						size="XS"
						variant="primaryHC"
						onClick={() => setShowLocationListOnMobile((prevState) => !prevState)}
						dataTestId="modern_google_maps_location_list_toggle_button"
					>
						<Typography variant="captionMedium" color="text.interactive">
							{showLocationListOnMobile ? 'Hide' : 'Show'} list
						</Typography>
					</TAGButton>
				</>
			)}
		</Box>
	);

	const locationList = (
		<Grid item container ref={listContainerRef} rowGap={isSmallScreen ? 1 : 2} md={12} sx={locationsListStyles}>
			{nearByFacilities.map((facility: BffFacility, index: number) => {
				const { scheduleAppointmentURL, schedulingSystem, revspringLocationValue = '' } = facility;
				const newFacility = facility as Facility;
				newFacility.scheduling = {
					...newFacility.scheduling,
					schedulingUrl: scheduleAppointmentURL,
				};
				const locationScheduling = {
					schedulingSystem: schedulingSystem as SchedulingSystemType,
				};
				const isInPilot = pilotLocations.has(revspringLocationValue ?? '');
				return (
					<LocationCard
						key={newFacility.code}
						id={`location-list-item-${newFacility.code}`}
						index={index}
						location={newFacility}
						isSmallScreen={isSmallScreen}
						distance={facility.location?.distance as number}
						defaultImage={defaultImage}
						selectedImage={selectedImage}
						googleMapsBusinessQuery={googleMapsBusinessQuery}
						facilityBasePath={facilityBasePath}
						isCurrentSelectedFacility={facility.code === selectedFacilityId}
						holidayHoursMessageText={holidayHoursMessageText ?? ''}
						locationScheduling={isInPilot ? locationScheduling : undefined}
						facilityBadgeLabel={facilityBadgeLabel}
						facilityButtonLabel={facilityButtonLabel}
						scheduleButton={scheduleButton}
						officeDetailsButton={officeDetailsButton}
						setSelectedFacilityId={setSelectedFacilityId}
						availableServicesTabs={availableServicesTabs}
					/>
				);
			})}
		</Grid>
	);

	const handleTouchStart = (event: React.TouchEvent) => {
		setTouchStart(event.touches[0].clientY);
	};

	const handleTouchMove = (event: React.TouchEvent) => {
		if (touchStart === null) return;

		const currentTouch = event.touches[0].clientY;
		const diff = touchStart - currentTouch;
		const threshold = window.innerHeight * 0.15; // Reduced to 15% for easier activation

		if (diff > threshold && !isFullScreen) {
			setIsFullScreen(true);
		} else if (diff < -threshold && isFullScreen) {
			setIsFullScreen(false);
		}
	};

	const handleTouchEnd = () => {
		setTouchStart(null);
	};

	return isSmallScreen ? (
		<>
			<CssBaseline />
			<Global
				styles={{
					'.MuiDrawer-root > .MuiPaper-root': {
						height: isFullScreen ? 'calc(100% - 14rem)' : '40%',
						overflow: 'visible',
						transition: 'height 0.3s ease',
						backgroundColor: theme.palette.backgrounds?.white,
					},
				}}
			/>
			{hasSelectedFacility ? (
				<Drawer
					anchor="bottom"
					open={showLocationListOnMobile}
					hideBackdrop
					PaperProps={{
						sx: drawerStyles,
					}}
				>
					{listToggler}
					{locationList}
				</Drawer>
			) : (
				<SwipeableDrawer
					anchor="bottom"
					open={showLocationListOnMobile}
					onClose={() => {
						setShowLocationListOnMobile(false);
						setIsFullScreen(false);
					}}
					onOpen={() => setShowLocationListOnMobile(true)}
					swipeAreaWidth={46}
					disableSwipeToOpen={true}
					disableDiscovery={true}
					hideBackdrop
					PaperProps={{
						sx: drawerStyles,
						...(showLocationListOnMobile && {
							onTouchStart: handleTouchStart,
							onTouchMove: handleTouchMove,
							onTouchEnd: handleTouchEnd,
						}),
					}}
				>
					{listToggler}
					{locationList}
				</SwipeableDrawer>
			)}
		</>
	) : (
		<Grid
			data-test-id="modern_google_maps_location_cards_container"
			item
			container
			md={12}
			sx={getLocationsCardContainerStyles({
				theme,
			})}
		>
			<Box sx={{ p: '0.5rem 1.5rem' }}>{locationCounter}</Box>
			{locationList}
		</Grid>
	);
};

export interface IModernGoogleMap {
	isSmallScreen?: boolean;
	fullWidth: boolean;
	shouldShowSearch: boolean;
	googleMapsSectionProps: IGoogleMapsSection;
	nearByLocationsCardsProps: INearbyLocationsCards;
	facilitiesData: IFacilitiesData;
	searchValue: string;
	setSearchValue: (value: string) => void;
	updateDataFromLocationSearch: (facilitiesByAddress: BffFacility[]) => void;
	handleRevertToCurrentFacility: () => void;
	noResultsContent?: Maybe<TMultiCardV2>;
}

export default function ModernGoogleMap({
	googleMapsSectionProps,
	nearByLocationsCardsProps,
	isSmallScreen = false,
	fullWidth,
	facilitiesData,
	updateDataFromLocationSearch,
	handleRevertToCurrentFacility,
	searchValue,
	setSearchValue,
	noResultsContent,
}: Readonly<IModernGoogleMap>) {
	const theme = useTheme();
	const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const listContainerRef = useRef<HTMLDivElement | null>(null);

	const onMapPinClick = (facilityId: string) => {
		if (setSelectedFacilityId) {
			setSelectedFacilityId(facilityId);
		}
		if (containerRef.current) {
			const containerElement = containerRef.current as HTMLElement;
			windowScrollToElement({
				targetElement: containerElement,
			});
		}

		const id = `location-list-item-${facilityId}`;
		const listSectionElement = document.getElementById(id);

		// Handles issue with sub-component scroll bar
		if (listSectionElement) {
			window.setTimeout(() => {
				if (!listContainerRef?.current) return;

				const listSectionElementTop = listSectionElement.getBoundingClientRect().top;
				const listContainerTop = listContainerRef.current.getBoundingClientRect().top;
				const scrollTop = listSectionElementTop - listContainerTop + listContainerRef.current.scrollTop;
				listContainerRef.current.scrollTo({
					top: scrollTop,
					behavior: 'smooth',
				});
			}, 350); // must wait for additional scrolling and content collapse to complete.
		}

		return facilityId;
	};

	if (facilitiesData.reqStatus !== RequestStatus.Success) {
		return <LoadingSkeleton theme={theme} fullWidth={fullWidth} isSmallScreen={isSmallScreen} />;
	}

	return (
		<Grid
			id="modern-map-container"
			data-test-id="modern_google_maps_grid_container"
			container
			ref={containerRef}
			direction={isSmallScreen ? 'column' : 'row'}
			sx={getMapAndLocationsComponentStyles(theme, fullWidth)}
		>
			{/* Locations cards container item with search */}
			<Grid
				data-test-id="modern_google_maps_locations_container"
				item
				container
				md={4}
				sx={getLocationsWrapStyles({ isSmallScreen })}
			>
				<LocationSearch
					updateDataFromLocationSearch={updateDataFromLocationSearch}
					revertToCurrentFacility={handleRevertToCurrentFacility}
					searchQueryValueFromUrl={searchValue}
					onSearchQueryChange={setSearchValue}
				/>

				{/* Location card */}
				<NearbyLocationsCards
					{...nearByLocationsCardsProps}
					selectedFacilityId={selectedFacilityId}
					setSelectedFacilityId={setSelectedFacilityId}
					listContainerRef={listContainerRef}
				/>
			</Grid>
			{/* maps item */}
			{facilitiesData.items.length === 0 && noResultsContent ? (
				<Grid item md={8} data-test-id="modern_google_maps_modern_map_no_result_content_section">
					<MultiCardV2 {...(noResultsContent as IMultiCard)} useSmallScreenCarouselDesign />
				</Grid>
			) : (
				<ModernGoogleMapSection
					{...googleMapsSectionProps}
					selectedFacilityId={selectedFacilityId}
					onPinClick={onMapPinClick}
				/>
			)}
		</Grid>
	);
}
